import {AdjacencyMap, NodesToAncestors} from './GraphUtilConstants';

interface NodeAndPath {
  nodeId: string;
  nodeIdsInPath: Set<string>;
}

/**
 * Compute all ancestors of all nodes in the graph, ignoring any loops.
 *
 * This function performs a DFS starting from the root node and collects all paths from the root node to
 * every other node in the graph. All the nodes on the paths betwwen the root and a node (including the node
 * itself) are considered the ancestors of the node. Note that this means that a node is its own ancestor.
 *
 * @param rootNodeId The ID of the root node in the graph.
 * @param adjacencyMap A mapping from node ID to the IDs of all adjacent nodes in the graph.
 * @returns A map of nodeIds to all ancestors of that node, as well as an updated adjacency map with loop edges removed (corresponding to the ancestors we return).
 *
 * For instance, for the following graph:
 *
 * A -> B -> C -> D
 *      |    |
 *      F <- E
 *
 * There is a loop B -> C -> E -> F -> B. Our adjacencyMapLoopsRemoved does not count the edge from F -> B,
 * so we return the following adjacency map:
 *
 * A: ['B']
 * B: ['C']
 * C: ['D', 'E']
 * D: []
 * E: ['F']
 * F: []
 *
 */
export default function getLoopIgnoringAncestorsAndEdges(
  rootNodeId: string,
  adjacencyMap: AdjacencyMap
): {nodeIdsToAncestors: NodesToAncestors; adjacencyMapLoopsRemoved: AdjacencyMap} {
  const adjacencyMapLoopsRemoved: AdjacencyMap = {};
  for (const [fromNodeId] of Object.entries(adjacencyMap)) {
    adjacencyMapLoopsRemoved[fromNodeId] = [];
  }
  const nodeIdToAncestors: {[nodeId: string]: Set<string>} = {[rootNodeId]: new Set([rootNodeId])};
  for (const [fromNodeId, toNodeIds] of Object.entries(adjacencyMap)) {
    for (const nodeId of [fromNodeId, ...toNodeIds]) {
      if (nodeIdToAncestors[nodeId] == null) {
        nodeIdToAncestors[nodeId] = new Set([nodeId]);
      }
    }
  }
  const stack: Array<NodeAndPath> = [{nodeId: rootNodeId, nodeIdsInPath: new Set([rootNodeId])}];
  while (stack.length > 0) {
    const {nodeId, nodeIdsInPath} = stack.pop()!;
    const neighbors = adjacencyMap[nodeId];
    if (neighbors == null) {
      continue;
    }
    for (const toNodeId of neighbors) {
      // If we've already seen all the nodes in this path, or it's part of a cycle, we don't need to revisit this node.
      if (nodeIdsInPath.has(toNodeId)) {
        continue;
      }
      nodeIdToAncestors[toNodeId] = new Set([...nodeIdToAncestors[toNodeId], ...nodeIdsInPath]);
      if (!adjacencyMapLoopsRemoved[nodeId].includes(toNodeId)) {
        adjacencyMapLoopsRemoved[nodeId].push(toNodeId);
      }
      stack.push({
        nodeId: toNodeId,
        nodeIdsInPath: new Set([...nodeIdsInPath, toNodeId]),
      });
    }
  }

  return {nodeIdsToAncestors: nodeIdToAncestors, adjacencyMapLoopsRemoved};
}
